import { useCallback, useEffect, useRef, useState } from 'react';
import {
  collection,
  DocumentData,
  documentId,
  getDocs,
  limit,
  orderBy,
  query,
  QueryDocumentSnapshot,
  QuerySnapshot,
  startAfter,
  Timestamp,
  where
} from 'firebase/firestore';
import { LongVideo } from '../types';
import { db } from '../backend/firebase';
import { formatCount } from '../backend/mediaService';
import { cacheJson, readCachedJson } from '../backend/cacheService';
import { parseTimestamp, formatRelativeTime } from '../utils/relativeTime';
import { withReadCounter } from '../utils/withReadCounter';

const PAGE_SIZE = 10;
const VIDEO_TYPES = ['long', 'video', 'longs', 'videos', 'long-video'];

interface FeedStream {
  cursor: QueryDocumentSnapshot | null;
  buffer: LongVideo[];
  exhausted: boolean;
}

interface FollowingStream extends FeedStream {
  ownerUids: string[];
}

interface UseLongsFeedOptions {
  currentUserId?: string;
  blockedUserIds: Set<string>;
}

const emptyStream = (): FeedStream => ({ cursor: null, buffer: [], exhausted: false });

const timestampValue = (value: unknown): number => parseTimestamp(value)?.getTime() ?? 0;

const viewsLabel = (value: unknown): string => {
  const count = typeof value === 'number' ? value : Number(value);
  const safeCount = Number.isFinite(count) && count > 0 ? count : 0;
  return `${formatCount(safeCount)} ${safeCount === 1 ? 'view' : 'views'}`;
};

const mapLongVideo = (snapshot: QueryDocumentSnapshot): LongVideo | null => {
  const item = snapshot.data();
  const type = String(item.type || '').trim().toLowerCase();
  const ownerUid = item.creatorUid || item.ownerUid || item.uploaderId || item.userId || '';
  const moderationStatus = String(item.moderationStatus || item.contentStatus || '').toLowerCase();

  if (
    !VIDEO_TYPES.includes(type) ||
    item.isDeleted === true ||
    item.isHidden === true ||
    item.hidden === true ||
    item.ownerSuspended === true ||
    item.accountSuspended === true ||
    item.visibility === 'private' ||
    ['hidden', 'removed', 'rejected', 'blocked'].includes(moderationStatus)
  ) {
    return null;
  }

  const videoUrl = item.mediaUrl || item.storagePath;
  if (typeof videoUrl !== 'string' || !videoUrl) return null;

  return {
    id: snapshot.id,
    ownerUid,
    clueId: item.clueId,
    title: item.title || 'Untitled video',
    description: item.caption || item.description || '',
    authorName: item.authorName || 'Clue200 Creator',
    authorUsername: item.authorUsername || 'creator',
    authorAvatar: item.authorAvatar || '',
    videoUrl,
    thumbnailUrl: item.thumbnailUrl || item.mediaUrl || '',
    duration: String(item.duration || ''),
    views: viewsLabel(item.viewCount ?? item.views),
    uploadedAt: formatRelativeTime(item.createdAt),
    createdAt: item.createdAt,
    followers: formatCount(Number(item.followerCount ?? item.fansCount ?? 0)),
    likes: Number(item.likeCount ?? item.likes ?? 0),
    dislikes: Number(item.dislikeCount ?? item.dislikes ?? 0),
    comments: Number(item.commentCount ?? item.commentsCount ?? 0),
    isFollowing: false
  };
};

const uniqueVideos = (items: LongVideo[]): LongVideo[] => {
  const byId = new Map<string, LongVideo>();
  items.forEach(item => {
    if (item.id) byId.set(item.id, item);
  });
  return Array.from(byId.values());
};

const cacheKeyForUser = (uid?: string) => `longs-first-page-${uid || 'anonymous'}`;

export function useLongsFeed({ currentUserId, blockedUserIds }: UseLongsFeedOptions) {
  const cacheKey = cacheKeyForUser(currentUserId);
  const cachedFeed = readCachedJson<LongVideo[]>(cacheKey) || [];
  const [videos, setVideos] = useState<LongVideo[]>(cachedFeed);
  const [isLoading, setIsLoading] = useState(cachedFeed.length === 0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState('');
  const [newVideos, setNewVideos] = useState<LongVideo[]>([]);

  const videosRef = useRef<LongVideo[]>(cachedFeed);
  const hasMoreRef = useRef(true);
  const newVideosRef = useRef<LongVideo[]>([]);
  const seenIdsRef = useRef(new Set(cachedFeed.map(video => video.id)));
  const queuedIdsRef = useRef(new Set<string>());
  const followingUidsRef = useRef(new Set<string>());
  const blockedUserIdsRef = useRef(blockedUserIds);
  const discoverRef = useRef<FeedStream>(emptyStream());
  const followingStreamsRef = useRef<FollowingStream[]>([]);
  const nextBatchRef = useRef<Promise<LongVideo[]> | null>(null);
  const fetchFailedRef = useRef(false);
  const loadNextBatchRef = useRef<() => Promise<LongVideo[]>>(async () => []);
  const retryRef = useRef<() => void>(() => {});
  const activeGenerationRef = useRef(0);
  const lastRefreshCheckRef = useRef(0);
  const currentUserIdRef = useRef(currentUserId);

  useEffect(() => {
    blockedUserIdsRef.current = blockedUserIds;
    const visibleVideos = videosRef.current.filter(video => !blockedUserIds.has(video.ownerUid || ''));
    if (visibleVideos.length !== videosRef.current.length) {
      videosRef.current = visibleVideos;
      setVideos(visibleVideos);
      void cacheJson(cacheKey, visibleVideos.slice(0, PAGE_SIZE).map(video => ({
        ...video,
        createdAt: parseTimestamp(video.createdAt)?.toISOString() || undefined
      })));
    }
    const visibleNewVideos = newVideosRef.current.filter(video => !blockedUserIds.has(video.ownerUid || ''));
    if (visibleNewVideos.length !== newVideosRef.current.length) {
      newVideosRef.current = visibleNewVideos;
      setNewVideos(visibleNewVideos);
    }
  }, [blockedUserIds, cacheKey]);

  const isEligible = useCallback((video: LongVideo) => {
    return !blockedUserIdsRef.current.has(video.ownerUid || '');
  }, []);

  const loadFollowingPage = useCallback(async (stream: FollowingStream) => {
    if (stream.exhausted || stream.buffer.length >= PAGE_SIZE) return;
    const constraints = [
      where('ownerUid', 'in', stream.ownerUids),
      where('status', '==', 'published'),
      where('isDeleted', '==', false),
      orderBy('createdAt', 'desc'),
      orderBy(documentId(), 'desc'),
      ...(stream.cursor ? [startAfter(stream.cursor)] : []),
      limit(PAGE_SIZE)
    ];
    const snapshot = await withReadCounter(
      'longs-following-feed',
      () => getDocs(query(collection(db, 'media'), ...constraints)),
      result => ({ reads: result.metadata.fromCache ? 0 : result.size })
    );

    stream.cursor = snapshot.docs.at(-1) || stream.cursor;
    stream.exhausted = snapshot.size < PAGE_SIZE;
    snapshot.docs.forEach(document => {
      const video = mapLongVideo(document);
      if (
        video &&
        video.ownerUid &&
        followingUidsRef.current.has(video.ownerUid) &&
        isEligible(video) &&
        !seenIdsRef.current.has(video.id) &&
        !queuedIdsRef.current.has(video.id) &&
        !stream.buffer.some(buffered => buffered.id === video.id)
      ) {
        stream.buffer.push(video);
      }
    });
  }, [isEligible]);

  const loadDiscoverPage = useCallback(async () => {
    const stream = discoverRef.current;
    if (stream.exhausted || stream.buffer.length >= PAGE_SIZE) return;
    const constraints = [
      where('type', 'in', VIDEO_TYPES),
      where('status', '==', 'published'),
      where('isDeleted', '==', false),
      orderBy('createdAt', 'desc'),
      orderBy(documentId(), 'desc'),
      ...(stream.cursor ? [startAfter(stream.cursor)] : []),
      limit(PAGE_SIZE)
    ];
    const snapshot = await withReadCounter(
      'longs-discover-feed',
      () => getDocs(query(collection(db, 'media'), ...constraints)),
      result => ({ reads: result.metadata.fromCache ? 0 : result.size })
    );

    stream.cursor = snapshot.docs.at(-1) || stream.cursor;
    stream.exhausted = snapshot.size < PAGE_SIZE;
    snapshot.docs.forEach(document => {
      const video = mapLongVideo(document);
      if (
        video &&
        video.ownerUid !== currentUserIdRef.current &&
        !followingUidsRef.current.has(video.ownerUid || '') &&
        isEligible(video) &&
        !seenIdsRef.current.has(video.id) &&
        !queuedIdsRef.current.has(video.id) &&
        !stream.buffer.some(buffered => buffered.id === video.id)
      ) {
        stream.buffer.push(video);
      }
    });
  }, [isEligible]);

  const takeFollowing = useCallback(async (count: number) => {
    const selected: LongVideo[] = [];
    const streams = followingStreamsRef.current;
    while (selected.length < count && streams.some(stream => !stream.exhausted || stream.buffer.length > 0)) {
      await Promise.all(streams.map(async stream => {
        while (
          stream.buffer.filter(video => !seenIdsRef.current.has(video.id) && !queuedIdsRef.current.has(video.id)).length < count - selected.length &&
          !stream.exhausted
        ) {
          await loadFollowingPage(stream);
        }
      }));

      const candidates = streams.flatMap(stream => stream.buffer
        .filter(video => !seenIdsRef.current.has(video.id) && !queuedIdsRef.current.has(video.id))
        .map(video => ({ stream, video })))
        .sort((a, b) => timestampValue(b.video.createdAt) - timestampValue(a.video.createdAt));

      const next = candidates[0];
      if (!next) break;
      next.stream.buffer = next.stream.buffer.filter(video => video.id !== next.video.id);
      selected.push(next.video);
    }
    return selected;
  }, [loadFollowingPage]);

  const takeDiscover = useCallback(async (count: number) => {
    const stream = discoverRef.current;
    const selected: LongVideo[] = [];
    while (selected.length < count && (!stream.exhausted || stream.buffer.length > 0)) {
      while (
        stream.buffer.filter(video => !seenIdsRef.current.has(video.id) && !queuedIdsRef.current.has(video.id)).length < count - selected.length &&
        !stream.exhausted
      ) {
        await loadDiscoverPage();
      }
      const next = stream.buffer.find(video => !seenIdsRef.current.has(video.id) && !queuedIdsRef.current.has(video.id));
      if (!next) break;
      stream.buffer = stream.buffer.filter(video => video.id !== next.id);
      selected.push(next);
    }
    return selected;
  }, [loadDiscoverPage]);

  const loadNextBatch = useCallback(async () => {
    const following = await takeFollowing(6);
    const discover = await takeDiscover(4);
    if (following.length < 6) {
      discover.push(...await takeDiscover(PAGE_SIZE - following.length - discover.length));
    }
    if (discover.length < 4) {
      following.push(...await takeFollowing(PAGE_SIZE - following.length - discover.length));
    }
    const batch = uniqueVideos([...following, ...discover])
      .filter(video => !seenIdsRef.current.has(video.id) && !queuedIdsRef.current.has(video.id))
      .slice(0, PAGE_SIZE);
    batch.forEach(video => queuedIdsRef.current.add(video.id));
    const more = followingStreamsRef.current.some(stream => !stream.exhausted || stream.buffer.length > 0) ||
      !discoverRef.current.exhausted || discoverRef.current.buffer.length > 0;
    hasMoreRef.current = more || batch.length > 0;
    setHasMore(hasMoreRef.current);
    return batch;
  }, [takeDiscover, takeFollowing]);

  useEffect(() => {
    loadNextBatchRef.current = loadNextBatch;
  }, [loadNextBatch]);

  const startPrefetch = useCallback(() => {
    if (nextBatchRef.current || !hasMoreRef.current) return;
    const generation = activeGenerationRef.current;
    const pending = loadNextBatchRef.current()
      .then(batch => {
        if (generation !== activeGenerationRef.current) return [];
        return batch;
      })
      .catch(fetchError => {
        if (generation === activeGenerationRef.current) {
          fetchFailedRef.current = true;
          setError(fetchError instanceof Error ? fetchError.message : 'Could not load Longs.');
          hasMoreRef.current = false;
          setHasMore(false);
        }
        return [];
      });
    nextBatchRef.current = pending;
  }, []);

  const appendVideos = useCallback((incoming: LongVideo[]) => {
    const unique = uniqueVideos(incoming)
      .filter(video => isEligible(video) && !seenIdsRef.current.has(video.id));
    incoming.forEach(video => {
      if (!isEligible(video)) queuedIdsRef.current.delete(video.id);
    });
    unique.forEach(video => {
      seenIdsRef.current.add(video.id);
      queuedIdsRef.current.delete(video.id);
    });
    const next = uniqueVideos([...videosRef.current, ...unique]);
    if (import.meta.env.DEV) {
      console.debug('[Longs feed IDs]', {
        rows: next.length,
        uniqueIds: new Set(next.map(video => video.id)).size
      });
    }
    videosRef.current = next;
    setVideos(next);
    void cacheJson(cacheKey, next.slice(0, PAGE_SIZE).map(video => ({
      ...video,
      createdAt: parseTimestamp(video.createdAt)?.toISOString() || undefined
    })));
    return unique;
  }, [cacheKey, isEligible]);

  const removeVideo = useCallback((id: string) => {
    const next = videosRef.current.filter(video => video.id !== id);
    videosRef.current = next;
    setVideos(next);
    void cacheJson(cacheKey, next.slice(0, PAGE_SIZE).map(video => ({
      ...video,
      createdAt: parseTimestamp(video.createdAt)?.toISOString() || undefined
    })));
  }, [cacheKey]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const queuedBatch = nextBatchRef.current;
      let batch = queuedBatch ? await queuedBatch : await loadNextBatchRef.current();
      if (queuedBatch && nextBatchRef.current === queuedBatch) nextBatchRef.current = null;
      if (fetchFailedRef.current) return;
      setError('');
      batch = batch.filter(video => !seenIdsRef.current.has(video.id));
      if (batch.length) appendVideos(batch);
      const streamsHaveMore = followingStreamsRef.current.some(stream => !stream.exhausted || stream.buffer.length > 0) ||
        !discoverRef.current.exhausted || discoverRef.current.buffer.length > 0;
      hasMoreRef.current = streamsHaveMore;
      setHasMore(streamsHaveMore);
      if (streamsHaveMore) startPrefetch();
    } catch (fetchError) {
      setError(fetchError instanceof Error ? fetchError.message : 'Could not load more Longs.');
    } finally {
      setLoadingMore(false);
    }
  }, [appendVideos, hasMore, loadingMore, startPrefetch]);

  const updateNewVideos = useCallback((items: LongVideo[]) => {
    newVideosRef.current = items;
    setNewVideos(items);
  }, []);

  const prependNewVideos = useCallback(() => {
    const fresh = newVideosRef.current.filter(video => !seenIdsRef.current.has(video.id));
    fresh.forEach(video => {
      seenIdsRef.current.add(video.id);
      queuedIdsRef.current.delete(video.id);
    });
    if (fresh.length) {
      const next = uniqueVideos([...fresh, ...videosRef.current]);
      videosRef.current = next;
      setVideos(next);
      void cacheJson(cacheKey, next.slice(0, PAGE_SIZE).map(video => ({
        ...video,
        createdAt: parseTimestamp(video.createdAt)?.toISOString() || undefined
      })));
    }
    updateNewVideos([]);
  }, [cacheKey, updateNewVideos]);

  const checkForNewVideos = useCallback(async (force = false, prepend = false) => {
    const now = Date.now();
    if (!force && now - lastRefreshCheckRef.current < 60_000) return;
    lastRefreshCheckRef.current = now;
    const newestTimestamp = videosRef.current.reduce<number>((newest, video) => {
      return Math.max(newest, timestampValue(video.createdAt));
    }, 0);
    if (!newestTimestamp) return;

    let snapshots: QuerySnapshot<DocumentData>[];
    try {
      const newestCreatedAt = Timestamp.fromMillis(newestTimestamp);
      snapshots = await Promise.all([
        withReadCounter(
          'longs-new-videos',
          () => getDocs(query(
            collection(db, 'media'),
            where('type', 'in', VIDEO_TYPES),
            where('status', '==', 'published'),
            where('isDeleted', '==', false),
            where('createdAt', '>', newestCreatedAt),
            orderBy('createdAt', 'desc'),
            orderBy(documentId(), 'desc'),
            limit(PAGE_SIZE)
          )),
          result => ({ reads: result.metadata.fromCache ? 0 : result.size })
        ),
        ...followingStreamsRef.current.map((stream, index) => withReadCounter(
          `longs-following-new-videos-${index + 1}`,
          () => getDocs(query(
            collection(db, 'media'),
            where('ownerUid', 'in', stream.ownerUids),
            where('status', '==', 'published'),
            where('isDeleted', '==', false),
            where('createdAt', '>', newestCreatedAt),
            orderBy('createdAt', 'desc'),
            orderBy(documentId(), 'desc'),
            limit(PAGE_SIZE)
          )),
          result => ({ reads: result.metadata.fromCache ? 0 : result.size })
        ))
      ]);
    } catch (refreshError) {
      setError(refreshError instanceof Error ? refreshError.message : 'Could not check for new Longs.');
      return;
    }
    const discovered = snapshots.flatMap(snapshot => snapshot.docs)
      .map(mapLongVideo)
      .filter((video): video is LongVideo => Boolean(video))
      .filter(video => video.ownerUid !== currentUserIdRef.current && isEligible(video));
    const prioritized = [
      ...discovered.filter(video => followingUidsRef.current.has(video.ownerUid || '')),
      ...discovered.filter(video => !followingUidsRef.current.has(video.ownerUid || ''))
    ];
    const merged = uniqueVideos([...prioritized, ...newVideosRef.current])
      .filter(video => !seenIdsRef.current.has(video.id));
    updateNewVideos(merged);
    if (prepend && merged.length) {
      const fresh = merged.filter(video => !seenIdsRef.current.has(video.id));
      fresh.forEach(video => seenIdsRef.current.add(video.id));
      const next = uniqueVideos([...fresh, ...videosRef.current]);
      videosRef.current = next;
      setVideos(next);
      updateNewVideos([]);
      void cacheJson(cacheKey, next.slice(0, PAGE_SIZE).map(video => ({
        ...video,
        createdAt: parseTimestamp(video.createdAt)?.toISOString() || undefined
      })));
    }
  }, [cacheKey, isEligible, updateNewVideos]);

  useEffect(() => {
    const generation = ++activeGenerationRef.current;
    let active = true;
    currentUserIdRef.current = currentUserId;
    const cached = readCachedJson<LongVideo[]>(cacheKey) || [];
    videosRef.current = cached;
    seenIdsRef.current = new Set(cached.map(video => video.id));
    queuedIdsRef.current = new Set();
    followingUidsRef.current = new Set();
    followingStreamsRef.current = [];
    discoverRef.current = emptyStream();
    nextBatchRef.current = null;
    fetchFailedRef.current = false;
    setVideos(cached);
    updateNewVideos([]);
    setError('');
    newVideosRef.current = [];
    setHasMore(true);
    hasMoreRef.current = true;
    setIsLoading(cached.length === 0);

    const initialize = async () => {
      const followingSnapshot = currentUserId
        ? await getDocs(collection(db, 'users', currentUserId, 'following'))
        : null;
      if (!active || generation !== activeGenerationRef.current) return;
      const followingUids = new Set<string>();
      followingSnapshot?.docs.forEach(document => {
        const uid = document.data().uid || document.id;
        if (uid) followingUids.add(uid);
      });
      followingUidsRef.current = followingUids;
      const uidList = Array.from(followingUids);
      followingStreamsRef.current = [];
      for (let index = 0; index < uidList.length; index += 30) {
        followingStreamsRef.current.push({
          ...emptyStream(),
          ownerUids: uidList.slice(index, index + 30)
        });
      }

      if (cached.length === 0) {
        const initialBatch = await loadNextBatchRef.current();
        if (!active || generation !== activeGenerationRef.current) return;
        appendVideos(initialBatch);
        setIsLoading(false);
      }
      if (generation === activeGenerationRef.current) {
        startPrefetch();
        if (cached.length) void checkForNewVideos(true);
      }
    };

    void initialize().catch(initializeError => {
      if (!active || generation !== activeGenerationRef.current) return;
      fetchFailedRef.current = true;
      hasMoreRef.current = false;
      setHasMore(false);
      setError(initializeError instanceof Error ? initializeError.message : 'Could not load Longs.');
      setIsLoading(false);
    });
    retryRef.current = () => {
      if (!active) return;
      discoverRef.current = emptyStream();
      followingStreamsRef.current = followingStreamsRef.current.map(stream => ({
        ...emptyStream(),
        ownerUids: stream.ownerUids
      }));
      queuedIdsRef.current.clear();
      nextBatchRef.current = null;
      fetchFailedRef.current = false;
      setError('');
      setHasMore(true);
      hasMoreRef.current = true;
      setIsLoading(videosRef.current.length === 0);
      void loadNextBatchRef.current().then(batch => {
        appendVideos(batch);
        setIsLoading(false);
        startPrefetch();
      }).catch(retryError => {
        fetchFailedRef.current = true;
        hasMoreRef.current = false;
        setHasMore(false);
        setError(retryError instanceof Error ? retryError.message : 'Could not load Longs.');
        setIsLoading(false);
      });
    };

    return () => {
      active = false;
    };
  }, [appendVideos, cacheKey, checkForNewVideos, currentUserId, startPrefetch]);

  useEffect(() => {
    const check = () => {
      if (document.visibilityState === 'visible') void checkForNewVideos();
    };
    const timer = window.setInterval(check, 60_000);
    window.addEventListener('focus', check);
    document.addEventListener('visibilitychange', check);
    window.addEventListener('pageshow', check);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', check);
      document.removeEventListener('visibilitychange', check);
      window.removeEventListener('pageshow', check);
    };
  }, [checkForNewVideos]);

  const refresh = useCallback(async (prepend = false) => {
    await checkForNewVideos(true, prepend);
  }, [checkForNewVideos]);

  const retry = useCallback(() => retryRef.current(), []);

  return {
    videos,
    isLoading,
    loadingMore,
    hasMore,
    error,
    newVideos,
    loadMore,
    refresh,
    prependNewVideos,
    retry,
    removeVideo
  };
}
